import React, { useState } from 'react'
import { Field, useForm, useFormState } from 'react-final-form'
import { defineMessages, FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import {
  getPossessivePronoun,
  getSubjectivePronoun,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import { getAllAvailableCodesSet } from '@lyrahealth-inc/shared-app-logic/src/features/providers/utils'
import { ICD10_PRIMARY_FIELD, ICD10_SECONDARY_FIELD } from '@lyrahealth-inc/shared-app-logic/src/models/icd10/constants'
import { AvailableCodes, ICD10 } from '@lyrahealth-inc/shared-app-logic/src/models/icd10/ICD10'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { AlertLoudIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/AlertLoudIcon'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { Title as TitleText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { AccessibilityRolesNative } from '@lyrahealth-inc/ui-core-crossplatform/src/constants'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import styles from './icd10s.module.scss'
import ICD10sSelection from './ICD10sSelection'

/**
 * Why ICD-10 codes are required, which determines the section copy:
 * - healthPlan: the client is using their medical plan for Lyra sessions
 * - selfPay: the client self-pays and submits for insurance reimbursement
 * - customer: the client's customer (e.g. Solera) submits Lyra sessions to the client's health plan
 */
export type ICD10sVariant =
  | { type: 'healthPlan' }
  | { type: 'selfPay'; clientFirstName: string; pronouns: string[] }
  | { type: 'customer'; customerName: string }

type ICD10sFieldProps = {
  availableCodes?: AvailableCodes
  variant?: ICD10sVariant
}

type ICD10sCopy = {
  title: string
  description: string
  primaryContext: string
}

const SecondaryCodeContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['8px'],
}))

const ErrorText = styled(BodyText)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginLeft: spacing['8px'],
}))

const Title = styled(TitleText)(({ theme: { spacing } }) => ({
  marginBottom: spacing['8px'],
}))

const SubLabelContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginTop: spacing['8px'],
  marginBottom: spacing['8px'],
}))

const Description = styled(BodyText)(({ theme: { spacing } }) => ({
  marginBottom: spacing['24px'],
}))

const ErrorContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  marginTop: spacing['8px'],
}))

const messages = defineMessages({
  requiredInformationTitle: {
    defaultMessage: 'Required Information for Health Plan',
    description: 'Title for the ICD-10 field section',
  },
  requiredInformationForSelfPayTitle: {
    defaultMessage: 'Required Information for Self-pay',
    description: 'Title for the ICD-10 field section for a self-pay client',
  },
  requiredInformationForCustomerTitle: {
    defaultMessage: 'Required Information for {customerName} clients',
    description:
      'Title for the ICD-10 field section for a client whose customer submits sessions to their health plan, e.g. Solera',
  },
  customerDescription: {
    defaultMessage:
      "{customerName} will submit Lyra sessions to the client's health plan. ICD-10 codes are required by the medical plan.",
    description:
      'Description explaining why ICD-10 codes are required for a client whose customer submits sessions to their health plan',
  },
  primaryDiagnosisCustomerContext: {
    defaultMessage: 'Required for {customerName} clients',
    description:
      'Context text for primary diagnosis field for a client whose customer submits sessions to their health plan',
  },
  medicalPlanDescription: {
    defaultMessage:
      "The client is currently using their medical plan for Lyra sessions. ICD-10's are required by their medical plan.",
    description: 'Description explaining why ICD-10 codes are required',
  },
  selfPayDescription: {
    defaultMessage:
      '{clientFirstName} is currently using self-pay for {possessivePronoun} Lyra sessions. ICD-10 codes are required so that {subjectivePronoun} can submit for insurance reimbursement.',
    description: 'Description explaining why ICD-10 codes are required for a self-pay client',
  },
  primaryMentalHealthDiagnosis: {
    defaultMessage: 'Primary Mental Health Diagnosis *',
    description: 'Label for primary mental health diagnosis field',
  },
  secondaryMentalHealthDiagnosis: {
    defaultMessage: 'Secondary Mental Health Diagnosis',
    description: 'Label for secondary mental health diagnosis field',
  },
  ifApplicable: {
    defaultMessage: '(if applicable)',
    description: 'Text indicating the field is optional',
  },
  primaryDiagnosisContext: {
    defaultMessage: 'Required for clients using their medical plan.',
    description: 'Context text for primary diagnosis field',
  },
  primaryDiagnosisSelfPayContext: {
    defaultMessage: 'Required for clients using self-pay.',
    description: 'Context text for primary diagnosis field for a self-pay client',
  },
  secondaryDiagnosisContext: {
    defaultMessage: 'e.g. substance abuse',
    description: 'Example text for secondary diagnosis field',
  },
  addICD10Code: {
    defaultMessage: 'Add ICD-10 Code',
    description: 'Button text for adding ICD-10 code',
  },
  thisFieldIsRequired: {
    defaultMessage: 'This field is required',
    description: 'Validation error message for required fields',
  },
})

const getCopy = (variant: ICD10sVariant, formatMessage: IntlShape['formatMessage']): ICD10sCopy => {
  switch (variant.type) {
    case 'selfPay': {
      const { clientFirstName, pronouns } = variant
      return {
        title: formatMessage(messages.requiredInformationForSelfPayTitle),
        description: formatMessage(messages.selfPayDescription, {
          clientFirstName,
          possessivePronoun: getPossessivePronoun(pronouns),
          subjectivePronoun: getSubjectivePronoun(pronouns),
        }),
        primaryContext: formatMessage(messages.primaryDiagnosisSelfPayContext),
      }
    }
    case 'customer': {
      const { customerName } = variant
      return {
        title: formatMessage(messages.requiredInformationForCustomerTitle, { customerName }),
        description: formatMessage(messages.customerDescription, { customerName }),
        primaryContext: formatMessage(messages.primaryDiagnosisCustomerContext, { customerName }),
      }
    }
    case 'healthPlan':
    default:
      return {
        title: formatMessage(messages.requiredInformationTitle),
        description: formatMessage(messages.medicalPlanDescription),
        primaryContext: formatMessage(messages.primaryDiagnosisContext),
      }
  }
}

const ICD10sField = ({
  availableCodes = { all: [], common: [] },
  variant = { type: 'healthPlan' },
}: ICD10sFieldProps) => {
  const [showModal, setShowModal] = useState(false)
  const [selectedField, setSelectedField] = useState(ICD10_PRIMARY_FIELD)
  const { change } = useForm()
  const { values, submitFailed } = useFormState()
  const { colors } = useTheme()
  const { formatMessage } = useIntl()

  // Helper function to check if a field should be shown based on dependencies
  const shouldShowField = (fieldName: string): boolean => {
    // For now, implement the existing logic dynamically
    // This could be extended to read dependency rules from schema metadata
    if (fieldName === ICD10_SECONDARY_FIELD) {
      // Secondary field only shows if primary field has content OR secondary already has content
      return Boolean(
        (validPrimaryicd10Field && validPrimaryicd10Field.length > 0) ||
        (validSecondaryicd10Field && validSecondaryicd10Field.length > 0),
      )
    }

    // Primary field and other fields always show
    return true
  }

  // set of current and valid codes
  const validCodesSet = getAllAvailableCodesSet(availableCodes)

  const getValidCodes = (arr: Array<ICD10 | undefined> | undefined): ICD10[] =>
    arr?.filter((entry): entry is ICD10 => !!entry && validCodesSet.has(entry.code)) ?? []

  // filtered primary and secondary ICD10 code object
  const primaryCodes = getValidCodes(values[ICD10_PRIMARY_FIELD])
  const secondaryCodes = getValidCodes(values[ICD10_SECONDARY_FIELD])

  const validPrimaryicd10Field = primaryCodes.length ? primaryCodes : undefined
  const validSecondaryicd10Field = secondaryCodes.length ? secondaryCodes : undefined

  const copy = getCopy(variant, formatMessage)

  const saveSelectedCodes = (codes: any) => {
    change(selectedField, codes)
    setShowModal(false)
  }

  const setAndShowModal = (field: any) => {
    setSelectedField(field)
    setShowModal(true)
  }

  return (
    <>
      <Modal
        onRequestClose={() => setShowModal(false)}
        showCloseIcon={false}
        visible={showModal}
        modalContents={
          <ICD10sSelection
            availableCodes={availableCodes}
            initialCodes={selectedField === ICD10_PRIMARY_FIELD ? validPrimaryicd10Field : validSecondaryicd10Field}
            multiSelect={selectedField !== ICD10_PRIMARY_FIELD}
            submitFunction={saveSelectedCodes}
            duplicateCodes={selectedField === ICD10_PRIMARY_FIELD ? validSecondaryicd10Field : validPrimaryicd10Field}
            closeModal={() => setShowModal(false)}
          />
        }
        scrollableStyle={{ padding: 24 }}
        scrollable
        scrollableModalHeight='auto'
        closeOnScrim
        customHeader={<></>}
      />

      <Title text={copy.title} size={TitleSize.LARGE} />
      <Description text={copy.description} />

      {[ICD10_PRIMARY_FIELD, ICD10_SECONDARY_FIELD].map((field) => {
        // Dynamic field visibility check
        if (!shouldShowField(field)) {
          return null
        }

        return (
          <div className='row' key={field}>
            <Field
              name={field}
              component={({ input: { value }, meta }: any) => {
                const showError = Boolean(meta?.error && (meta?.touched || submitFailed))
                return (
                  <div
                    className={`col-xs-12 ${styles.section}`}
                    data-test-id={`ICD10sField-${field === ICD10_PRIMARY_FIELD ? 'primaryField' : 'secondaryField'}`}
                  >
                    <View>
                      {field === ICD10_PRIMARY_FIELD ? (
                        <>
                          <BodyText
                            text={<FormattedMessage {...messages.primaryMentalHealthDiagnosis} />}
                            color={showError ? colors.textError : colors.textPrimary}
                          />
                        </>
                      ) : (
                        <SecondaryCodeContainer>
                          <BodyText text={<FormattedMessage {...messages.secondaryMentalHealthDiagnosis} />} />
                          <BodyText
                            text={<FormattedMessage {...messages.ifApplicable} />}
                            color={colors.textSecondary}
                          />
                        </SecondaryCodeContainer>
                      )}
                    </View>
                    <SubLabelContainer>
                      {field === ICD10_PRIMARY_FIELD ? (
                        <BodyText text={copy.primaryContext} color={colors.textSecondary} />
                      ) : (
                        <BodyText
                          text={<FormattedMessage {...messages.secondaryDiagnosisContext} />}
                          color={colors.textSecondary}
                        />
                      )}
                    </SubLabelContainer>
                    <div className={styles['code-tokens-container']}>
                      {Array.isArray(value) &&
                        value
                          .filter((entry: ICD10 | undefined): entry is ICD10 => entry !== undefined)
                          .map((code: ICD10) => (
                            <div
                              className={`${styles['code-token']} ${styles['code-token-removeable']}`}
                              key={code.value}
                            >
                              {code.value}
                              <button
                                tabIndex={-1}
                                aria-label='Remove'
                                className={styles.close}
                                type='button'
                                onClick={() => {
                                  const newValue = value.filter((c: ICD10) => c.code !== code.code)
                                  change(field, newValue)
                                }}
                              >
                                <span aria-hidden='true'>×</span>
                              </button>
                            </div>
                          ))}
                    </div>
                    <PrimaryButton
                      testID={tID(`ICD10sForm-add${field === ICD10_PRIMARY_FIELD ? 'Primary' : 'Secondary'}`)}
                      onPress={() => setAndShowModal(field)}
                      text={<FormattedMessage {...messages.addICD10Code} />}
                    />
                    {showError && (
                      <ErrorContainer>
                        <AlertLoudIcon fillColor={colors.iconError} />
                        <ErrorText
                          size={BodyTextSize.SMALL}
                          text={meta?.error}
                          color={colors.textError}
                          testID={tID(`field-${field}-error-message`)}
                          accessibilityLabel={
                            typeof meta?.error === 'string' ? meta.error : messages.thisFieldIsRequired.defaultMessage
                          }
                          accessibilityRole={AccessibilityRolesNative.ALERT}
                        />
                      </ErrorContainer>
                    )}
                  </div>
                )
              }}
            />
          </div>
        )
      })}
    </>
  )
}

export default ICD10sField
